import React, { useEffect, useMemo, useState } from "react";
import {
  Badge,
  Box,
  Button,
  Center,
  Group,
  LoadingOverlay,
  Pagination,
  ScrollArea,
  Select,
  Table,
  Text,
  TextInput,
} from "@mantine/core";
import { IconDownload, IconSearch } from "@tabler/icons-react";
import { url } from "../../settings/linkUrl";
import chrome from "../AdminPageChrome.module.css";

interface GownRow {
  stdid: string;
  number_true: string;
  name: string;
  lname: string;
  vut: string;
  levelk: string;
  programe: string;
  set_size: string;
  set_size_label: string;
  order_type: string;
  receive_type: string;
  address: string;
  phone: string;
  size_note: string;
}

export default function AdministratorGownPage() {
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [items, setItems] = useState<GownRow[]>([]);
  const [search, setSearch] = useState("");
  const [sizeFilter, setSizeFilter] = useState<string | null>("all");
  const [receiveFilter, setReceiveFilter] = useState<string | null>("all");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(50);

  const authHeaders = () => ({
    Authorization: `Bearer ${localStorage.getItem("token")}`,
  });

  const loadData = () => {
    setLoading(true);
    fetch(`${url}listname/gownlist`, {
      method: "GET",
      headers: authHeaders(),
    })
      .then((response) => {
        if (!response.ok) throw new Error(response.statusText);
        return response.json();
      })
      .then((json) => {
        setItems(Array.isArray(json?.items) ? json.items : []);
      })
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    loadData();
  }, []);

  const filtered = useMemo(() => {
    const q = search.toLowerCase().trim();
    return items.filter((row) => {
      if (sizeFilter && sizeFilter !== "all" && row.set_size !== sizeFilter) {
        return false;
      }
      if (receiveFilter === "pickup" && row.receive_type !== "มารับด้วยตนเอง") {
        return false;
      }
      if (receiveFilter === "post" && row.receive_type !== "จัดส่งทางไปรษณีย์") {
        return false;
      }
      if (!q) return true;
      const haystack = [
        row.stdid,
        row.number_true,
        row.name,
        row.lname,
        row.levelk,
        row.programe,
        row.set_size_label,
        row.phone,
        row.address,
      ]
        .join(" ")
        .toLowerCase();
      return haystack.includes(q);
    });
  }, [items, search, sizeFilter, receiveFilter]);

  const sizeOptions = useMemo(() => {
    const sizes = Array.from(new Set(items.map((row) => row.set_size))).sort();
    return [
      { value: "all", label: "ทุกไซส์" },
      ...sizes.map((size) => ({
        value: size,
        label: items.find((row) => row.set_size === size)?.set_size_label || size,
      })),
    ];
  }, [items]);

  const indexOfLast = currentPage * itemsPerPage;
  const indexOfFirst = indexOfLast - itemsPerPage;
  const currentRows = filtered.slice(indexOfFirst, indexOfLast);
  const totalPages = Math.max(1, Math.ceil(filtered.length / itemsPerPage));

  const handleExport = async () => {
    setExporting(true);
    try {
      const response = await fetch(`${url}listname/gownexport`, {
        method: "GET",
        headers: authHeaders(),
      });
      if (!response.ok) throw new Error("export failed");
      const blob = await response.blob();
      const objectUrl = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = objectUrl;
      link.download = `gown-list-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(objectUrl);
    } catch {
      /* ignore */
    } finally {
      setExporting(false);
    }
  };

  const receiveBadge = (receiveType: string) => {
    if (receiveType === "มารับด้วยตนเอง") {
      return <Badge color="blue" variant="light">{receiveType}</Badge>;
    }
    if (receiveType === "จัดส่งทางไปรษณีย์") {
      return <Badge color="orange" variant="light">{receiveType}</Badge>;
    }
    return <Badge color="gray" variant="light">{receiveType || "-"}</Badge>;
  };

  const rows = currentRows.map((row, index) => (
    <Table.Tr key={row.stdid}>
      <Table.Td style={{ textAlign: "center" }}>{indexOfFirst + index + 1}</Table.Td>
      <Table.Td>{row.stdid}</Table.Td>
      <Table.Td style={{ textAlign: "center" }}>{row.number_true}</Table.Td>
      <Table.Td>
        {row.name} {row.lname}
      </Table.Td>
      <Table.Td>{row.levelk}</Table.Td>
      <Table.Td>{row.programe}</Table.Td>
      <Table.Td style={{ textAlign: "center" }}>
        <Badge color="blue" variant="light">
          {row.set_size_label}
        </Badge>
      </Table.Td>
      <Table.Td style={{ textAlign: "center" }}>{row.order_type}</Table.Td>
      <Table.Td style={{ textAlign: "center" }}>{receiveBadge(row.receive_type)}</Table.Td>
      <Table.Td maw={260}>{row.address}</Table.Td>
      <Table.Td>{row.phone}</Table.Td>
    </Table.Tr>
  ));

  return (
    <>
      <LoadingOverlay visible={loading} />
      <header className={chrome.pageHeader}>
        <Group justify="space-between" align="flex-start" wrap="wrap">
          <div>
            <h1 className={chrome.title}>รายการครุยวิทยฐานะ</h1>
            <p className={chrome.subtitle}>
              บัณฑิตที่ชำระเงินแล้วและสั่งซื้อ/เช่าครุย — ไซส์ ที่อยู่จัดส่ง และวิธีรับชุด
            </p>
          </div>
          <Button
            color="blue"
            leftSection={<IconDownload size={16} />}
            loading={exporting}
            onClick={handleExport}
          >
            ดาวน์โหลด Excel
          </Button>
        </Group>
      </header>

      <div className="admin-panel" style={{ marginBottom: "1rem" }}>
        <Group grow wrap="wrap" align="flex-end">
          <TextInput
            label="ค้นหา"
            placeholder="รหัส ชื่อ คณะ ไซส์ ที่อยู่ เบอร์โทร"
            leftSection={<IconSearch size="0.9rem" stroke={1.5} />}
            value={search}
            onChange={(event) => {
              setSearch(event.currentTarget.value);
              setCurrentPage(1);
            }}
          />
          <Select
            label="ไซส์"
            data={sizeOptions}
            value={sizeFilter}
            onChange={(value) => {
              setSizeFilter(value);
              setCurrentPage(1);
            }}
            allowDeselect={false}
          />
          <Select
            label="วิธีรับชุด"
            data={[
              { value: "all", label: "ทั้งหมด" },
              { value: "pickup", label: "มารับด้วยตนเอง" },
              { value: "post", label: "จัดส่งทางไปรษณีย์" },
            ]}
            value={receiveFilter}
            onChange={(value) => {
              setReceiveFilter(value);
              setCurrentPage(1);
            }}
            allowDeselect={false}
          />
        </Group>
        <Text size="sm" c="dimmed" mt="sm">
          แสดง {filtered.length.toLocaleString("th-TH")} รายการ จากทั้งหมด{" "}
          {items.length.toLocaleString("th-TH")} คน
        </Text>
      </div>

      <div className="admin-table-wrap">
        <ScrollArea>
          <Table fz="sm" striped highlightOnHover withRowBorders>
            <Table.Thead>
              <Table.Tr>
                <Table.Th style={{ textAlign: "center" }}>#</Table.Th>
                <Table.Th>รหัสประจำตัว</Table.Th>
                <Table.Th style={{ textAlign: "center" }}>ลำดับ</Table.Th>
                <Table.Th>ชื่อ - นามสกุล</Table.Th>
                <Table.Th>คณะ</Table.Th>
                <Table.Th>สาขาวิชา</Table.Th>
                <Table.Th style={{ textAlign: "center" }}>ไซส์</Table.Th>
                <Table.Th style={{ textAlign: "center" }}>ซื้อ/เช่า</Table.Th>
                <Table.Th style={{ textAlign: "center" }}>วิธีรับชุด</Table.Th>
                <Table.Th>ที่อยู่จัดส่ง</Table.Th>
                <Table.Th>เบอร์โทร</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {rows.length > 0 ? (
                rows
              ) : (
                <Table.Tr>
                  <Table.Td colSpan={11}>
                    <div className="admin-empty">ไม่พบข้อมูลครุย</div>
                  </Table.Td>
                </Table.Tr>
              )}
            </Table.Tbody>
          </Table>
        </ScrollArea>
      </div>

      <Box className={chrome.footerBar}>
        <Select
          style={{ width: "110px" }}
          size="sm"
          label="แสดง"
          value={itemsPerPage.toString()}
          onChange={(value) => {
            setItemsPerPage(Number(value) || 50);
            setCurrentPage(1);
          }}
          data={[
            { value: "20", label: "20" },
            { value: "50", label: "50" },
            { value: "100", label: "100" },
            { value: `${Math.max(filtered.length, 1)}`, label: "ทั้งหมด" },
          ]}
          allowDeselect={false}
        />
        <Pagination
          total={totalPages}
          value={currentPage}
          onChange={setCurrentPage}
          size="sm"
          color="blue"
          withEdges
        />
      </Box>
    </>
  );
}
